// Mirrors the server-side limits in ContactMobileNumberVerificationController so the
// UI can disable actions and show progress without waiting for a rejected request.
export const MAX_VERIFICATION_ATTEMPTS = 5;
export const RESEND_COOLDOWN_SECONDS = 60;
export const VERIFICATION_CODE_LENGTH = 6;

export const ViewState = {
   INITIAL: 'INITIAL',
   EDIT: 'EDIT',
   VALIDATE: 'VALIDATE',
   SUCCESS: 'SUCCESS',
   LOCKED: 'LOCKED',
} as const;

export type ViewState = typeof ViewState[keyof typeof ViewState];

export interface MobileNumberDetails {
   mobileNumber?: string;
   mobileNumberVerifiedDate?: string;
}

export interface MobileNumberValidationDetails {
   errorMessage: string | null;
   expiresOn: string | null;
   isSuccess: boolean;
   mobileNumber: string;
   // Set only when the request was refused because the verification is locked out.
   lockoutSecondsRemaining?: number | null;
}

export interface MobileNumberVerifyDetails {
   errorMessage: string | null;
   attempts: number;
   isSuccess: boolean;
   mobileNumber: string;
   lockoutSecondsRemaining?: number | null;
}

// GET /ContactMobileNumberVerification/current — the session already in flight when
// the component mounts (e.g. the practitioner refreshed mid-verification).
export interface MobileNumberCurrentVerificationDetails {
   mobileNumber?: string;
   attempts?: number;
   isLockedOut?: boolean;
   lockoutSecondsRemaining?: number | null;
   startDate?: string;
}

// The API sends lockoutSecondsRemaining only while a verification is locked out, so its
// presence — not an attempts comparison — is what routes the UI into the locked view.
export const getLockoutSeconds = (data?: {
   lockoutSecondsRemaining?: number | null;
}): number | null =>
   data && typeof data.lockoutSecondsRemaining === 'number'
      ? data.lockoutSecondsRemaining
      : null;

// Root class for every view. Only an explicit false marks the field invalid, so a
// field whose validity has not been evaluated yet (the host's formValidity map has no
// entry until its first submit) stays neutral instead of rendering red on first paint.
// Returns whole class names rather than concatenating fragments - joining them without
// a separator is what silently breaks the invalid styling in the shared TextInput.
export const getFieldClassName = (inputValid?: boolean): string =>
   inputValid === false
      ? 'mobile-number-text-field mobile-number-text-field-invalid'
      : 'mobile-number-text-field';

// Renders a remaining-seconds count as m:ss.
export const formatCountdown = (totalSeconds: number): string => {
   const safeSeconds = Math.max(0, Math.floor(totalSeconds));
   const minutes = Math.floor(safeSeconds / 60);
   const seconds = safeSeconds % 60;

   return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

// Seconds left on the resend cooldown for a session that started at startDate.
export const remainingCooldownSeconds = (startDate?: string): number => {
   if (!startDate) return 0;

   const startedOn = new Date(startDate).getTime();
   if (Number.isNaN(startedOn)) return 0;

   const elapsed = Math.floor((Date.now() - startedOn) / 1000);
   return Math.min(RESEND_COOLDOWN_SECONDS, Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed));
};

// The label that replaced the "Not Verified" chip. The chip sat beside the field label,
// away from the input it described, and gave no instruction on how to resolve itself.
export const MOBILE_NUMBER_REQUIRED_LABEL = 'Mobile Number Required';

// Shared second line: the rule behind every one of these warnings.
export const MOBILE_NUMBER_ONE_ACCOUNT_RULE =
   'The Board requires each practitioner to have a mobile number linked only to their account.';

export const MOBILE_NUMBER_DUPLICATE_HEADLINE =
   'This mobile number is linked to another account.';

// The closing line differs by view - the read-only view has to send the user to Change,
// the edit form already has them there.
export const MOBILE_NUMBER_DUPLICATE_VIEW_ACTION =
   'Select Change to update your mobile number before continuing with your application.';

export const MOBILE_NUMBER_DUPLICATE_EDIT_ACTION =
   'Enter a different mobile number to continue with your application.';

export const MOBILE_NUMBER_MISSING_MESSAGES = [
   'A mobile number is required.',
   MOBILE_NUMBER_ONE_ACCOUNT_RULE,
   'Select Change to add your mobile number before continuing with your application.',
];

export const MOBILE_NUMBER_UNVERIFIED_MESSAGES = [
   'This mobile number has not been verified.',
   'Select Change to verify your mobile number before continuing with your application.',
];

// The duplicate check is the one API failure with its own designed copy, so it is matched
// on the message the server sends (PgdbMobileNumberValidationService: "This mobile number
// is already registered to {count} other user(s)."). Every other failure - bad format,
// lockout - renders the API's own wording, so a reworded server message degrades to plain
// text rather than to the wrong explanation.
const DUPLICATE_ERROR_PATTERN = /already registered to \d+ other user/i;

// Every validate/send-OTP response echoes back the number it was evaluated against, and
// isSuccess is absent only on the empty initial/reset state. Both checks are needed
// because validateMobileNumberResult is a single slot in the store, written by both the
// read-only view's on-load check and the edit form's debounced check - so a result is only
// ever read by the view whose number produced it. Without this, a check still in flight
// when the user hits Change lands after the reset and marks a number they never typed.
export const getResultForMobileNumber = <
   T extends { mobileNumber?: string | null; isSuccess?: boolean }
>(
   details: T | undefined,
   mobileNumber?: string
): T | undefined =>
   details &&
   details.isSuccess !== undefined &&
   (details.mobileNumber || '') === (mobileNumber || '')
      ? details
      : undefined;

// Maps an API error message onto the lines the warning box renders.
export const getWarningMessages = (
   errorMessage?: string | null,
   duplicateActionMessage: string = MOBILE_NUMBER_DUPLICATE_VIEW_ACTION
): string[] => {
   if (!errorMessage) return [];

   if (!DUPLICATE_ERROR_PATTERN.test(errorMessage)) return [errorMessage];

   return [
      MOBILE_NUMBER_DUPLICATE_HEADLINE,
      MOBILE_NUMBER_ONE_ACCOUNT_RULE,
      duplicateActionMessage,
   ];
};

// The read-only view knows only that a number is unverified - the reason comes from the
// validation the container runs against the saved number on load. Until that result
// arrives the generic unverified copy stands in, which is true whatever the reason.
export const getViewWarningMessages = (
   mobileNumber?: string,
   validationDetails?: MobileNumberValidationDetails
): string[] => {
   if (!mobileNumber) return MOBILE_NUMBER_MISSING_MESSAGES;

   const result = getResultForMobileNumber(validationDetails, mobileNumber);
   if (!result || result.isSuccess) return MOBILE_NUMBER_UNVERIFIED_MESSAGES;

   const messages = getWarningMessages(result.errorMessage);
   return messages.length ? messages : MOBILE_NUMBER_UNVERIFIED_MESSAGES;
};
